/**
 * Store reducer. Pure — one call per action. Snapshots do NOT flow
 * through here at 30 Hz; the sampled `hud.sample` fires at 10 Hz.
 *
 * Rejection timeouts and cast-flash timeouts are handled by dropping
 * old entries in the reducer at each `hud.tick` — the controller
 * dispatches ticks at the HUD sample rate.
 */

import type { Action, CastErrorEntry, RejectionEntry, StoreState, KeypressFlash, PhaseState } from "./types.js";
import { INITIAL_STATE } from "./types.js";
import { toTick, type MatchPhase, type Tick } from "@pit/protocol";

/** Rejection toast lifetime (ms). */
export const REJECTION_TTL_MS = 3000;

/** Cast-flash duration (ms) — matches the wp7 spec's 150ms hint. */
export const CAST_FLASH_TTL_MS = 150;

/** Default countdown duration in ticks (5 seconds @ 30 Hz = 150 ticks). */
export const DEFAULT_COUNTDOWN_TICKS = 150;

let rejectionIdSeq = 1;

/**
 * Monotonic phase rank order (waiting < countdown < active < ended).
 * Prevents reordered network messages or late match-created/joined events
 * from regressing an already advanced match phase within the same match.
 */
export function phaseRank(phase: MatchPhase): number {
  switch (phase) {
    case "waiting":
      return 0;
    case "countdown":
      return 1;
    case "active":
      return 2;
    case "ended":
      return 3;
  }
}

/** Pure reducer. */
export function reducer(state: StoreState, action: Action): StoreState {
  switch (action.type) {
    case "connection.status": {
      return { ...state, connection: action.next };
    }
    case "match.created": {
      // If matchId changed (e.g. entering a new match without explicit match.leave),
      // reset phase state to clean slate. Otherwise preserve monotonic progression.
      const isSameMatch = state.matchId !== null && state.matchId === action.matchId;
      const nextPhase: PhaseState =
        isSameMatch && phaseRank(state.phase.phase) > phaseRank("waiting")
          ? state.phase
          : { phase: "waiting", winner: null, endsAtTick: null };

      return {
        ...state,
        matchId: action.matchId,
        joinUrl: action.joinUrl,
        joinError: null,
        serverError: null,
        phase: nextPhase,
        mapSlug: action.mapSlug,
      };
    }
    case "match.joined": {
      return {
        ...state,
        matchId: action.matchId,
        ownSeat: action.ownSeat,
        joinError: null,
      };
    }
    case "match.phase": {
      // #1175: Enforce monotonic phase ordering unless explicitly reset via match.leave
      // or entering a new match. If a delayed/reordered match.phase carries an earlier phase,
      // do not regress.
      const currentRank = phaseRank(state.phase.phase);
      const incomingRank = phaseRank(action.phase);

      const nextPhase: PhaseState =
        incomingRank >= currentRank
          ? { phase: action.phase, winner: action.winner, endsAtTick: action.endsAtTick }
          : state.phase;

      return {
        ...state,
        phase: nextPhase,
        roster: action.roster.length > 0 ? action.roster : state.roster,
        damageDealt: action.damageDealt.length > 0 ? action.damageDealt : state.damageDealt,
        mapSlug: action.mapSlug,
      };
    }
    case "match.leave": {
      return {
        ...INITIAL_STATE,
        connection: state.connection,
        // #855: the chat SETTING is a persisted user preference, not match
        // state — leaving a match must not silently turn chat back on. The
        // MUTE list is the opposite (per-match, session-only, deliberately
        // not persisted), so it resets with everything else.
        chatEnabled: state.chatEnabled,
      };
    }
    case "match.champion": {
      return { ...state, ownChampionId: action.championId, ownLoadout: action.loadout };
    }
    case "hud.sample": {
      const snap = action.snapshot;
      const snapPhase = snap.phase;
      const currentRank = phaseRank(state.phase.phase);
      const snapRank = phaseRank(snapPhase);

      // #1175: Auto-resolve phase if active snapshot or countdown timer received
      // out-of-order or if a MatchPhase broadcast was dropped.
      let nextPhase: PhaseState = state.phase;
      if (snapRank > currentRank) {
        let endsAtTick: Tick | null = state.phase.endsAtTick;
        if (snapPhase === "countdown" && endsAtTick === null) {
          // If MatchPhase(countdown) was dropped, estimate deadline from current tick
          endsAtTick = toTick(snap.tick + DEFAULT_COUNTDOWN_TICKS);
        } else if (snapPhase !== "countdown") {
          endsAtTick = null;
        }

        nextPhase = {
          phase: snapPhase,
          winner: snap.winner,
          endsAtTick,
        };
      }

      return {
        ...state,
        hudSnapshot: snap,
        phase: nextPhase,
      };
    }
    case "hud.tick": {
      const now = Date.now();
      const rejections = pruneRejections(state.rejections, now);
      const flashes = pruneFlashes(state.castFlashes, now);
      if (rejections === state.rejections && flashes === state.castFlashes) {
        return { ...state, hudTick: state.hudTick + 1 };
      }
      return {
        ...state,
        hudTick: state.hudTick + 1,
        rejections,
        castFlashes: flashes,
      };
    }
    case "rejection.add": {
      const entry: RejectionEntry = {
        id: rejectionIdSeq++,
        reason: action.reason,
        ts: action.ts,
      };
      return { ...state, rejections: [...state.rejections, entry] };
    }
    case "rejection.drop": {
      return {
        ...state,
        rejections: state.rejections.filter((r) => r.id !== action.id),
      };
    }
    case "castFlash.add": {
      const flash: KeypressFlash = { slot: action.slot, ts: action.ts };
      return { ...state, castFlashes: [...state.castFlashes, flash] };
    }
    case "castFlash.drop": {
      return {
        ...state,
        castFlashes: state.castFlashes.filter((f) => f.ts !== action.ts),
      };
    }
    case "castError.add": {
      // #787: COALESCE by reason — mashing an unaffordable key must show ONE
      // message, not N. An existing entry for this reason has its `ts` (and
      // payload, e.g. a different pool) refreshed in place, which also restarts
      // the notice's dismissal timer; a new reason appends.
      //
      // The two reason comparisons here are lint-suppressed because
      // `CastBlockReason` has exactly ONE member today (`insufficientResource`),
      // so TS can prove them constant. They are NOT dead: out-of-range (#352),
      // school-lock and invalid-target are the intended next arms (see
      // `input/castability.ts`), and coalescing must key off the reason the
      // moment a second one exists. Deleting them would be a latent bug.
      const existing = state.castErrors.findIndex(
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- one-arm union today; see above
        (e) => e.block.reason === action.block.reason,
      );
      const entry: CastErrorEntry = { block: action.block, ts: action.ts };
      if (existing === -1) {
        return { ...state, castErrors: [...state.castErrors, entry] };
      }
      const next = state.castErrors.slice();
      next[existing] = entry;
      return { ...state, castErrors: next };
    }
    case "castError.drop": {
      return {
        ...state,
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- one-arm union today; see `castError.add`
        castErrors: state.castErrors.filter((e) => e.block.reason !== action.reason),
      };
    }
    case "joinError.set": {
      return { ...state, joinError: action.reason };
    }
    case "serverError": {
      return {
        ...state,
        serverError: { code: action.code, ts: action.ts },
      };
    }
    case "serverError.clear": {
      return { ...state, serverError: null };
    }
    case "practice.botList": {
      return { ...state, practiceBots: action.bots };
    }
    case "chat.setOpen": {
      // #855: opening is REFUSED while chat is disabled. This is the load-
      // bearing half of the setting: `InputSurface`'s `if (chatOpen) return;`
      // guard swallows every keystroke it sees, so "chat is off" must mean
      // that guard is unreachable — enforced here, once, rather than at every
      // site that might dispatch this action.
      if (action.open && !state.chatEnabled) return state;
      // #833: identity-return when already in the requested state so a
      // redundant close (e.g. Escape on an already-closed box) doesn't
      // re-render every store consumer.
      if (state.chatOpen === action.open) return state;
      return { ...state, chatOpen: action.open };
    }
    case "chat.setEnabled": {
      if (state.chatEnabled === action.enabled) return state;
      // Turning chat off force-closes an open box in the same commit, so the
      // "disabled ⇒ not open" invariant holds even if the player disables
      // chat from the settings overlay while the box is up.
      return { ...state, chatEnabled: action.enabled, chatOpen: false };
    }
    case "chat.setSeatMuted": {
      const muted = state.mutedSeats.includes(action.seat);
      if (muted === action.muted) return state;
      const next = action.muted
        ? [...state.mutedSeats, action.seat].sort((a, b) => a - b)
        : state.mutedSeats.filter((s) => s !== action.seat);
      return { ...state, mutedSeats: next };
    }
  }
}

function pruneRejections(
  entries: readonly RejectionEntry[],
  now: number,
): readonly RejectionEntry[] {
  const kept = entries.filter((e) => now - e.ts < REJECTION_TTL_MS);
  return kept.length === entries.length ? entries : kept;
}

function pruneFlashes(entries: readonly KeypressFlash[], now: number): readonly KeypressFlash[] {
  const kept = entries.filter((e) => now - e.ts < CAST_FLASH_TTL_MS);
  return kept.length === entries.length ? entries : kept;
}
